import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Popconfirm, Space, Table } from "antd";
import PageHeader from "Components/PageHeader/PageHeader";
import { AuthContext } from "Contexts/AuthContext";
import AlertService from "Network/DataService/AlertService";
import { tablePagination, tableScroll } from "PageBuilder/PageBuildConfigs";
import React, { useCallback, useContext, useEffect, useState } from "react";
import Highlighter from "react-highlight-words";
import { Link, useParams } from "react-router-dom";
import { isObjectId } from "Utils/Utils";
import "./AlertsPage.scss";
import AlertsPageDisplay from "./AlertsPage.styled";

export default function AlertsPage() {
  // fixed state stuff
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  // context stuff
  const { user, userType } = useContext(AuthContext);

  // filter route param
  const { itemId } = useParams();

  // tablesearch
  const [searchText, setSearchText] = useState(``);
  const [searchedColumn, setSearchedColumn] = useState(``);

  // ====================================================================
  // ============================== fixed cruds
  // ====================================================================
  const getItems = useCallback(async () => {
    if (userType === `admin`) {
      if (itemId && isObjectId(itemId)) {
        await AlertService.getParentAlerts({
          id: itemId,
          onSuccess: (res) => {
            let data = res.data;
            data = data.map((d) => {
              return { ...d, key: d.id };
            });
            setItems(data);
          },
          setLoading,
        });
      } else {
        await AlertService.get({
          onSuccess: (res) => {
            let data = res.data;
            data = data.map((d) => {
              return { ...d, key: d.id };
            });
            setItems(data);
          },
          setLoading,
        });
      }
    } else {
      const { id } = user;
      await AlertService.getParentAlerts({
        id,
        onSuccess: (res) => {
          let data = res.data;
          data = data.map((d) => {
            return { ...d, key: d.id };
          });
          setItems(data);
        },
        setLoading,
      });
    }
  }, [itemId, user, userType]);

  const deleteItem = async (record) => {
    const { id: itemId, memberId } = record;
    await AlertService.delete({
      id: memberId,
      fk: itemId,
      onSuccess: () => {
        getItems();
      },
      onFail: () => {
        getItems();
      },
      setLoading,
    });
  };

  useEffect(() => {
    getItems();
  }, [getItems]);

  // ====================================================================
  // ============================== table search functions
  // ====================================================================
  let searchInput;
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText(``);
  };

  let columns = [
    {
      title: "Device",
      dataIndex: "device",
      search: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      search: true,
    },
    {
      title: "Details",
      dataIndex: "details",
      search: true,
    },
    {
      title: "URL",
      dataIndex: "url",
      search: true,
    },
    {
      title: "Website",
      dataIndex: "hostname",
      search: true,
    },

    {
      title: "Date",
      dataIndex: "createdAt",
      width: 150,
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text) => (
        <span>{new Date(text).toLocaleDateString("en-US")}</span>
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <span>
          <Space>
            {userType === `admin` && (
              <Link to={`/parents/${record.memberId}`}>
                <Button>View Parent</Button>
              </Link>
            )}
            <Popconfirm
              title="Are you sure delete this item?"
              onConfirm={() => {
                deleteItem(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" type="primary" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </span>
      ),
    },
  ];
  columns = columns.map((c) => {
    c.defaultSortOrder = `descend`;
    if (c.search) {
      c = { ...c, ...getColumnSearchProps(c.dataIndex) };
    }
    return c;
  });
  return (
    <AlertsPageDisplay>
      <PageHeader title="Alerts"></PageHeader>
      <Table
        style={{ height: `calc(calc(var(--vh, 1vh) * 100) - 220px)` }}
        columns={columns}
        dataSource={items}
        pagination={tablePagination}
        scroll={tableScroll}
        loading={loading}
        bordered
      />
    </AlertsPageDisplay>
  );
}
